import { BLOC_LABEL } from "@features/pointage/config/constants";
import type { GeolocationPosition } from "@features/pointage/hooks/useGeolocation";
import useGeolocation from "@features/pointage/hooks/useGeolocation";
import { usePointageContext, usePointageSelf } from "@features/pointage/hooks/usePointageContext";
import type { PointageBloc } from "@features/pointage/types/pointageSelf";
import { useToast } from "@lib/hooks/useToast";
import { cn } from "@lib/utils/cn";
import { Check, Loader2, MapPin } from "lucide-react";
import { useMemo, useState } from "react";

const BLOCS: readonly PointageBloc[] = ["MATIN", "APRES_MIDI"];

const DAYS_BACK = 6;

interface SelectorDay {
  readonly iso: string;
  readonly label: string;
  readonly isToday: boolean;
}

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildDays(count: number): SelectorDay[] {
  const today = new Date();
  const days: SelectorDay[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push({
      iso: toISODate(d),
      label: new Intl.DateTimeFormat("fr-FR", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      }).format(d),
      isToday: i === 0,
    });
  }
  return days;
}

function splitKey(key: string): [string, PointageBloc] {
  const [date, bloc] = key.split("|");
  return [date, bloc as PointageBloc];
}

/**
 * DemiJourneeSelector — DEMI_JOURNEE mode: check matin / après-midi for today
 * and past days, then point each checked half-day (1 PRESENCE pointage each).
 */
export function DemiJourneeSelector() {
  const geolocation = useGeolocation();
  const toast = useToast();
  const { mutateAsync, isPending } = usePointageSelf();

  const days = useMemo(() => buildDays(DAYS_BACK + 1), []);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const toggle = (iso: string, bloc: PointageBloc) => {
    setSelected((prev) => {
      const key = `${iso}|${bloc}`;
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handlePointer = async () => {
    let position: GeolocationPosition | null = null;
    try {
      position = await geolocation.getCurrentPosition();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Géolocalisation impossible");
      return;
    }
    if (!position || position.latitude === 0 || position.longitude === 0) {
      toast.error("Position GPS non fiable. Réessayez.");
      return;
    }

    const coords = {
      latitude: position.latitude,
      longitude: position.longitude,
      accuracy: position.accuracy,
    };
    for (const key of selected) {
      const [date, bloc] = splitKey(key);
      await mutateAsync({ date, bloc, ...coords });
    }
    setSelected(new Set());
  };

  return (
    <div className="px-4 py-3 space-y-3">
      <p className="text-xs text-base-content/60">
        Cochez les demi-journées à pointer (aujourd'hui et jours passés), puis validez.
      </p>
      <ul className="space-y-1.5">
        {days.map((day) => (
          <DayRow key={day.iso} day={day} selected={selected} onToggle={toggle} />
        ))}
      </ul>
      <button
        type="button"
        onClick={handlePointer}
        disabled={isPending || selected.size === 0}
        className={cn(
          "btn btn-primary btn-sm gap-2 w-full",
          (isPending || selected.size === 0) && "btn-disabled pointer-events-none",
        )}
      >
        {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
        {isPending
          ? "Pointage en cours…"
          : `Pointer la sélection${selected.size > 0 ? ` (${selected.size})` : ""}`}
      </button>
    </div>
  );
}

interface DayRowProps {
  day: SelectorDay;
  selected: ReadonlySet<string>;
  onToggle: (iso: string, bloc: PointageBloc) => void;
}

function DayRow({ day, selected, onToggle }: DayRowProps) {
  const { data } = usePointageContext({ date: day.iso });
  const ready = !!data;

  return (
    <li className="flex items-center justify-between gap-2">
      <span
        className={cn(
          "text-xs font-medium w-20 shrink-0",
          day.isToday ? "text-primary" : "text-base-content/70",
        )}
      >
        {day.label}
        {day.isToday ? " · Auj." : ""}
      </span>
      <span className="flex items-center gap-1.5">
        {BLOCS.map((bloc) => {
          const key = `${day.iso}|${bloc}`;
          const pointed = data?.pointages.some((p) => p.type === "PRESENCE" && p.bloc === bloc);
          const expected = data?.expectedSlots.some((s) => s.bloc === bloc);
          const isSelected = selected.has(key);
          const disabled = !ready || pointed || !expected;

          let label = BLOC_LABEL[bloc];
          if (pointed) label = `Pointé ${BLOC_LABEL[bloc].toLowerCase()}`;
          else if (!expected && ready) label = "—";

          return (
            <button
              key={bloc}
              type="button"
              disabled={disabled}
              onClick={() => onToggle(day.iso, bloc)}
              className={cn(
                "btn btn-xs gap-1 px-2.5",
                pointed
                  ? "btn-success text-success-content pointer-events-none opacity-80"
                  : !expected && ready
                    ? "btn-ghost text-base-content/30 pointer-events-none"
                    : isSelected
                      ? "btn-primary"
                      : "btn-outline btn-base-200",
                !ready && "btn-ghost opacity-40 pointer-events-none",
              )}
            >
              {pointed ? <Check className="w-3 h-3" /> : null}
              {!ready ? "…" : label}
            </button>
          );
        })}
      </span>
    </li>
  );
}
